import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  DlmsAttributes,
  EventCodeMappingDto,
  MeterModelDataOut,
  PermissionsEnum,
} from '../../../../../api/api-reference';
import { generateFileNameAppendix } from '../../../../../helper/download-filename';
import { AuthService } from '../../../../../services/auth.service';
import { FileService } from '../../../../../services/file.service';

@Component({
  selector: 'app-meter-model-event-codes',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
  ],
  templateUrl: './meter-model-event-codes.component.html',
  styleUrl: './meter-model-event-codes.component.scss',
})
export class MeterModelEventCodesComponent implements OnInit {
  readonly eventCodeRegisters: DlmsAttributes[] = [
    DlmsAttributes.Data_EventCode1Value,
    DlmsAttributes.Data_EventCode2Value,
    DlmsAttributes.Data_EventCode3Value,
    DlmsAttributes.Data_EventCode4Value,
    DlmsAttributes.Data_EventCode5Value,
    DlmsAttributes.Data_EventCode6Value,
  ];

  PermissionEnum = PermissionsEnum;
  meterModel: MeterModelDataOut = new MeterModelDataOut();
  meterModelId = '';
  activeRegister: DlmsAttributes = DlmsAttributes.Data_EventCode1Value;

  mappings: EventCodeMappingDto[] = [];
  // Ids of the mappings the user deleted, kept here until Save sends them in one call.
  pendingDeletions = new Set<string>();

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private fileService: FileService,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.meterModel.model = history.state['model'];
    this.meterModel.firmwareVersion = history.state['firmwareVersion'];

    this.route.params.subscribe(params => {
      this.meterModelId = params['id'];
      this.loadMappings();
    });
  }

  download(): void {
    const fileNameApendix = generateFileNameAppendix();

    this.client.downloadEventCodeMappings(this.meterModelId).subscribe({
      next: response => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `EventCodeMapping${fileNameApendix}`,
        );
      },
      error: err => this.toastr.error(err),
    });
  }

  navigate(route: string): void {
    this.router.navigate([route]);
  }

  goBack(): void {
    this.location.back();
  }

  getRegisterNumber(register: DlmsAttributes): number {
    return this.eventCodeRegisters.indexOf(register) + 1;
  }

  getMappings(register: DlmsAttributes): EventCodeMappingDto[] {
    return this.mappings.filter(
      mapping => mapping.attribute === register && !this.pendingDeletions.has(mapping.id),
    );
  }

  get hasPendingDeletions(): boolean {
    return this.pendingDeletions.size > 0;
  }

  deleteMapping(mapping: EventCodeMappingDto): void {
    this.pendingDeletions.add(mapping.id);
  }

  save(): void {
    if (!this.hasPendingDeletions) return;

    this.client.deleteEventCodeMappings([...this.pendingDeletions]).subscribe({
      next: response => {
        this.toastr.success(response);
        this.pendingDeletions.clear();
        this.loadMappings();
      },
      error: err => this.toastr.error(err),
    });
  }

  private loadMappings(): void {
    this.client.getEventCodeMappings(this.meterModelId).subscribe({
      next: mappings => (this.mappings = mappings),
      error: err => this.toastr.error(err),
    });
  }
}
